import { makeArticle, saintRelated } from "./learnFactory";

export const pageSaintBrainTarget = makeArticle({
  slug: "what-part-of-brain-does-saint-target",
  title: "What Part of the Brain Does SAINT® Target? | Neuro Wellness",
  description:
    "SAINT® TMS targets the left dorsolateral prefrontal cortex, personalized to each patient using fMRI. Learn how brain targeting works in SAINT®.",
  canonical: "/learn/what-part-of-brain-does-saint-target",
  h1: "What Part of the Brain Does SAINT® TMS Target?",
  breadcrumbLabel: "SAINT Brain Target",
  shortAnswer:
    "SAINT® TMS targets the left dorsolateral prefrontal cortex (DLPFC) — a brain region involved in mood regulation. Unlike conventional TMS, which targets this area using scalp measurements, SAINT® uses a functional MRI (fMRI) to identify the specific subregion of the DLPFC that has the strongest functional connection to deeper mood-regulating circuits in your brain. This personalized targeting is a key feature of SAINT®. The target is unique to each patient, chosen from their own brain scan rather than a population average.",
  keyTakeaways: [
    "SAINT® targets the left dorsolateral prefrontal cortex (DLPFC).",
    "The target is personalized using fMRI, not scalp measurements.",
    "The fMRI identifies the subregion with the strongest connection to mood circuits.",
    "Each patient's target is unique to their own brain.",
    "This personalized targeting distinguishes SAINT® from conventional TMS.",
  ],
  sections: [
    {
      heading: "The Left Dorsolateral Prefrontal Cortex",
      paragraphs: [
        "SAINT® targets the left dorsolateral prefrontal cortex (DLPFC), a brain region involved in mood regulation, executive function, and emotional control. The DLPFC is underactive in many people with depression, and stimulating it with TMS is thought to normalize activity in broader mood-regulating networks.",
      ],
    },
    {
      heading: "How fMRI Personalizes the Target",
      paragraphs: [
        "Conventional TMS targets the DLPFC using scalp measurements — typically measuring from the spot that makes the thumb twitch (the motor threshold location) and moving 5.5 cm forward. This is a population average, not personalized. SAINT® replaces this approach with a functional MRI that maps how different brain regions connect functionally. A proprietary algorithm identifies the specific subregion of your DLPFC with the strongest functional connection to the subgenual anterior cingulate cortex — a deeper brain region involved in mood regulation.",
      ],
    },
    {
      heading: "Why Personalized Targeting Matters",
      paragraphs: [
        "The 'right' spot for TMS varies from brain to brain. Two people may have their optimal stimulation sites centimeters apart. By using fMRI to find each patient's individualized target, SAINT® aims to stimulate the precise subregion most likely to affect mood circuits. This is one of the key innovations developed at Stanford.",
      ],
    },
  ],
  faqs: [
    {
      question: "What part of the brain does SAINT TMS target?",
      answer:
        "SAINT® targets the left dorsolateral prefrontal cortex (DLPFC), a region involved in mood regulation. Unlike conventional TMS, SAINT® uses fMRI to personalize the exact stimulation site within the DLPFC for each patient.",
    },
    {
      question: "How does fMRI targeting work for SAINT?",
      answer:
        "The fMRI maps functional connectivity between brain regions. A proprietary algorithm identifies the specific subregion of your DLPFC with the strongest connection to deeper mood-regulating circuits. This becomes your personalized target, unique to your brain.",
    },
    {
      question: "How is SAINT targeting different from regular TMS?",
      answer:
        "Conventional TMS targets the DLPFC using scalp measurements (a population average). SAINT® uses fMRI to find each patient's individualized target — the specific subregion most connected to mood circuits. This personalized approach is a key innovation of SAINT®.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
  ],
  relatedLinks: [
    ...saintRelated,
    {
      title: "BEAM F3 vs fMRI targeting",
      href: "/learn/beam-f3-vs-fmri-tms",
      description:
        "How head-measurement targeting differs from an fMRI-guided target",
    },
  ],
});

export const pageSaintBetweenSessions = makeArticle({
  slug: "what-happens-between-saint-sessions",
  title: "What Happens Between SAINT® TMS Sessions? | Neuro Wellness",
  description:
    "Between SAINT® sessions, patients rest for 50 minutes. Learn what to do during breaks, what to expect, and how the treatment day is structured.",
  canonical: "/learn/what-happens-between-saint-sessions",
  h1: "What Happens Between SAINT® TMS Sessions?",
  breadcrumbLabel: "Between SAINT Sessions",
  shortAnswer:
    "Between SAINT® sessions, patients take a 50-minute break. During breaks, most patients read, work on a laptop, rest, or relax in the clinic. The breaks are a structured part of the protocol — they allow the brain to consolidate the effects of each stimulation session before the next one begins. Patients stay at the clinic for the full day, as each day includes 10 sessions with 50-minute breaks. Most patients remain awake and alert between sessions. The breaks are not optional.",
  keyTakeaways: [
    "50-minute breaks between each session are part of the protocol.",
    "Patients read, work, rest, or relax during breaks.",
    "Breaks allow the brain to consolidate stimulation effects.",
    "Patients stay at the clinic for the full day.",
    "The breaks are not optional — they are structured.",
  ],
  sections: [
    {
      heading: "The 50-Minute Break",
      paragraphs: [
        "SAINT® delivers 10 sessions per day, each about 10 minutes, separated by 50-minute breaks. The breaks are a structured part of the protocol — they are not optional downtime. Research suggests the breaks allow the brain to consolidate the effects of each stimulation session, which may contribute to the accelerated response seen in SAINT®.",
      ],
    },
    {
      heading: "What Patients Do During Breaks",
      paragraphs: ["During the 50-minute breaks, most patients:"],
      list: [
        "Read a book or magazine",
        "Work on a laptop (Wi-Fi is available)",
        "Rest or close their eyes",
        "Listen to music or podcasts",
        "Have a snack or beverage",
        "Chat with the treatment team",
      ],
    },
    {
      heading: "The Full Treatment Day",
      paragraphs: [
        "A typical SAINT® treatment day includes 10 sessions with 50-minute breaks, taking most of the day. Patients arrive in the morning and leave in the late afternoon. The exact schedule varies by clinic, but plan to spend most of each day at the clinic for 5 consecutive days. The 5-day protocol totals 50 sessions, and before it begins a psychiatric evaluation and an fMRI scan are required and scheduled in advance.",
      ],
    },
  ],
  faqs: [
    {
      question: "What happens between SAINT sessions?",
      answer:
        "Between sessions, patients take a 50-minute break. Most read, work on a laptop, rest, or relax in the clinic. The breaks are a structured part of the protocol — they allow the brain to consolidate stimulation effects. Patients stay at the clinic for the full day.",
    },
    {
      question: "Can I work between SAINT sessions?",
      answer:
        "Yes. Many patients work on a laptop during the 50-minute breaks. Wi-Fi is available. However, each day includes 10 sessions taking most of the day, so plan accordingly. Some patients take the week off from work entirely.",
    },
    {
      question: "How long are the breaks between SAINT sessions?",
      answer:
        "Breaks are 50 minutes between each session. With 10 sessions per day, this means you spend most of each day at the clinic. The breaks are part of the protocol, not optional downtime.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
  ],
  relatedLinks: saintRelated,
});

export const pageSaintStartQuickly = makeArticle({
  slug: "how-quickly-can-i-start-saint",
  title: "How Quickly Can I Start SAINT® TMS? | Neuro Wellness",
  description:
    "How quickly can you start SAINT® TMS? Learn the timeline from consultation to treatment, including MRI scheduling and the evaluation process.",
  canonical: "/learn/how-quickly-can-i-start-saint",
  h1: "How Quickly Can I Start SAINT® TMS?",
  breadcrumbLabel: "Starting SAINT Quickly",
  shortAnswer:
    "At Neuro Wellness, self-pay patients can typically move from consultation to treatment within a few days to two weeks, depending on fMRI scanner availability and treatment-suite scheduling. The process has three steps: a free clinical evaluation (in person or via telehealth), a resting-state fMRI brain scan scheduled locally or upon arrival, and the 5-day treatment week. Treatment blocks are scheduled Monday through Friday or across seven-day care-team availability. We do not promise a guaranteed start date before your evaluation and scan are complete. Insurance verification and prior authorization, if applicable, may add days to weeks depending on your carrier.",
  keyTakeaways: [
    "Three steps: consultation evaluation, fMRI brain scan, and 5-day treatment week.",
    "Self-pay patients often start within days to two weeks of their evaluation.",
    "fMRI scan scheduling and processing must be completed before treatment begins.",
    "Insurance verification or prior authorization may add days to weeks.",
    "Consultations clarify exact timeline without promising a guaranteed start date.",
  ],
  sections: [
    {
      heading:
        "How quickly can I start SAINT\u00ae TMS? The realistic timeline",
      paragraphs: [
        "Because SAINT\u00ae requires a personalized functional MRI scan before treatment, treatment cannot start the same day as your initial inquiry. However, because the treatment itself takes only five consecutive days (rather than six weeks), the overall timeline from inquiry to completion is significantly shorter than conventional TMS.",
        "For self-pay patients, the typical timeline from first call to day one of treatment is a few days to two weeks. The main variable is scheduling the fMRI scan and allowing time for Magnus Medical's software to process the imaging data and compute your personalized target.",
      ],
    },
    {
      heading: "The Steps Before Treatment",
      paragraphs: [
        "Before starting SAINT®, three operational steps must be completed:",
      ],
      list: [
        "Clinical Evaluation — a free consultation with our clinical team (in person or via telehealth) to confirm candidacy and review medical/MRI safety.",
        "fMRI Brain Scan — a structural and resting-state functional MRI scan to identify your personalized stimulation target. Out-of-state patients can complete this upon arrival or at a partner imaging center before traveling.",
        "Treatment Week Scheduling — reserving a private treatment suite for 5 consecutive days (10 sessions per day with 50-minute breaks).",
        "Insurance Verification / Prior Authorization (if applicable) — confirming coverage and submitting prior auth documentation if using insurance.",
      ],
    },
    {
      heading: "Self-Pay vs Insurance Timeline",
      paragraphs: [
        "Self-pay patients move fastest because no insurance prior authorization is required. Once your evaluation confirms candidacy and your fMRI scan is completed, treatment can begin as soon as a suite is open.",
        "If you are seeking insurance coverage or single-case agreement, the prior authorization process typically takes 1 to 4 weeks depending on your plan. We verify your insurance benefits for free at the outset so you know where your plan stands.",
      ],
    },
  ],
  faqs: [
    {
      question: "How quickly can I start SAINT TMS?",
      answer:
        "Self-pay patients can often start within days to weeks, depending on scheduling. The steps before treatment are consultation, fMRI, and scheduling the 5-day week. Insurance verification may add time. Contact us to discuss your specific timeline.",
    },
    {
      question: "How long does it take to get approved for SAINT?",
      answer:
        "If using insurance, benefits verification and prior authorization can take days to weeks depending on your plan. Self-pay patients can often start sooner since no authorization is needed. We recommend starting the verification process early.",
    },
    {
      question: "Can I start SAINT the same week as my consultation?",
      answer:
        "Starting the same week is uncommon because the fMRI must be completed and processed before treatment. However, self-pay patients with scheduling flexibility can sometimes start within days to weeks. Contact us to discuss expedited scheduling.",
    },
  ],
  sources: [],
  relatedLinks: saintRelated,
});

export const pageSaintTravelCompanion = makeArticle({
  slug: "can-someone-travel-with-me-for-saint",
  title: "Can Someone Travel With Me for SAINT® TMS? | Neuro Wellness",
  description:
    "Yes — a family member or companion can travel with you for SAINT® treatment. Learn what companions should expect during the treatment week.",
  canonical: "/learn/can-someone-travel-with-me-for-saint",
  h1: "Can Someone Travel With Me for SAINT® TMS?",
  breadcrumbLabel: "Traveling With a Companion",
  shortAnswer:
    "Yes, a family member, friend, or caregiver can travel with you for SAINT® TMS treatment. Because SAINT® uses no anesthesia or sedation, a companion is optional — you can typically drive yourself. However, many patients appreciate having a companion for support during the 5-day treatment week. Companions can wait at the clinic or explore the Coral Springs area during sessions. If you're traveling from out of town, your companion can stay at the same hotel. There is no medical requirement for a companion, but emotional support during treatment can be valuable.",
  keyTakeaways: [
    "Yes, a companion can travel with you for SAINT®.",
    "A companion is optional — no medical requirement.",
    "Companions can wait at the clinic or explore the area.",
    "Many patients appreciate emotional support during the week.",
    "Companions can stay at the same hotel.",
  ],
  sections: [
    {
      heading: "Companions Are Welcome",
      paragraphs: [
        "A family member, friend, or caregiver can travel with you for SAINT® treatment. Because SAINT® uses no anesthesia or sedation, a companion is not medically required — you can typically drive yourself and manage the treatment week independently. However, many patients appreciate having a companion for emotional support, especially during an intensive 5-day treatment.",
      ],
    },
    {
      heading: "What Companions Should Expect",
      paragraphs: ["During the treatment week, your companion can:"],
      list: [
        "Wait at the clinic during sessions (space permitting)",
        "Explore Coral Springs and South Florida between sessions",
        "Stay at the same hotel and share meals",
        "Provide emotional support during the intensive week",
        "Help with logistics, meals, and transportation",
      ],
    },
    {
      heading: "No Medical Requirement",
      paragraphs: [
        "Unlike ECT or SPRAVATO®, which require a driver due to anesthesia or dissociation, SAINT® does not require a companion. You can drive yourself to and from treatment. A companion is purely optional — some patients prefer to go through treatment alone, while others value the support.",
      ],
    },
  ],
  faqs: [
    {
      question: "Can someone travel with me for SAINT treatment?",
      answer:
        "Yes. A family member, friend, or caregiver can travel with you. A companion is optional — SAINT® uses no anesthesia, so no driver is required. Many patients appreciate emotional support during the 5-day week. Companions can wait at the clinic or explore the area.",
    },
    {
      question: "Do I need someone to drive me for SAINT?",
      answer:
        "No. Because SAINT® uses no anesthesia or sedation, you can typically drive yourself. A driver is not required. This differs from ECT and SPRAVATO®, which require a driver.",
    },
    {
      question: "What should my caregiver or spouse expect during SAINT?",
      answer:
        "Your companion can wait at the clinic, explore the area, or return to the hotel between sessions. Each day takes most of the day due to 10 sessions with breaks. Companions provide emotional support and help with logistics. No medical caregiving is required.",
    },
  ],
  sources: [],
  relatedLinks: saintRelated,
});
